//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import "../src/view/Login/LoginPage";
import "../src/view/Users/UsersMainView";
import "../src/view/Users/AddNewUser";
import "../src/view/Users/EditUser/EditUserTabView";
import "../src/view/Export/ExportExcel";
import "../src/view/Reporting/AuditDashboard";
import "../src/view/Workflow/WorkflowMainView";
import "../src/view/Workflow/EditWorkflowTabView";
import "../src/view/Workflow/CloneWorkflowTabView";
import "../src/view/Workflow/FilePatternView";
import "../src/view/WorkflowTemplates/WorkflowTemplateMainView";
import "../src/view/WorkflowTemplates/WorkflowTemplateGeneralView";
import "../src/view/WorkflowTemplates/WorkflowTemplateSettingsTab";
import "../src/view/WorkflowTemplates/WorkflowTemplateInfoTab";
import "../src/view/WorkflowTemplates/WorkflowTemplateNotificationTab";
import "../src/view/WorkflowTemplates/WorkflowTemplateStepsTab";
import "../src/view/WorkflowTemplates/WorkflowTemplateSchedulerTab";
import "../src/view/BusinessUnits/BusinessUnitsMainView";
import "../src/view/BusinessUnits/AddBusinessUnit";
import "../src/view/BusinessUnits/EditBusinessUnit";
import "../src/view/BusinessUnits/InstitutionsMainView";
import "../src/view/BusinessUnits/InstitutionDetailView";
import "../src/view/BusinessUnits/AddInstitution";
import "../src/view/BusinessUnits/EditInstitution";
import "./view/IpWhilelist/IpWhitelistMainView";
import "../src/view/Login/Callback";
import Router, { Context, RouteTreeNode, routingMixin } from "@jack-henry/web-component-router";
import { setJhaWcTheme, JHA_THEMES } from "@banno/jha-wc/src/styles/theme-provider";
import "./view/SideBar/SideNavBar";
import Cookies from "js-cookie";
import "./view/Login/ResetPassword";
import "./view/Login/Health";
import "./view/Login/SetPassword";
import { removeAllCookies } from "./utils/removeAllCookies";
import { setPictureSessionCookie } from "./utils/setUserSessionCookies";
import { AppRouters } from "./utils/app-router";
import {
    FORCED_LOGOUT_NOTICES,
    openForcedLogoutNotice,
    type ForcedLogoutNotice,
} from "./Components/JhftJhaDialog";
import { routerConfig } from "./routers";
import {
    decryptValue,
    encryptValue,
    isPublicAuthPath,
    hasExpiredIdleStamp,
    isIdlePastMax,
    isSessionExpired,
    parseLastActiveMs,
    remainingIdleMs,
    sessionRefreshDelayMs,
    classifySessionRefreshDue,
    readPostRequestPayload,
} from "./utils/app-utils";
import { getRequest, postRequest } from "./utils/jha-fetch";
import { Apis } from "./utils/api-constants";
import { GlobalConstants, GlobalVariables } from "./utils/constants";
import { getErrorMessage } from "./utils/getErrorMessage";
import { showToast } from "@banno/jha-wc/src/js/utils/toast-util.js";
import { launchDarkly } from "./utils/launchdarkly-service";
import { getLaunchDarklyConfig } from "./utils/launchdarkly-config";
import { createUserContext, updateLaunchDarklyUserContext } from "./utils/launchdarkly-helpers";

// LOCALHOST ONLY. JhftLab is a developer page. This import is omitted from production builds.
if (import.meta.env.DEV) {
    void import("./view/Lab/JhftLab");
}

/**
 * An example element.
 *
 * @slot - This element has a slot
 * @csspart button - The button
 */
@customElement("main-element")
class MainElement extends routingMixin(LitElement) {
    router: Router;

    constructor() {
        super();
        this.router = AppRouters;
    }

    static readonly styles = css`
        #mainDiv {
            display: flex;
            flex-direction: row;
            flex: 1;
            width: 100%;
        }
        /* Sidebar dropdowns (e.g. avatar menu) extend over the main pane; without z-index the
           later flex sibling (#slotBg) paints on top and clips menu content against the grid. */
        #mainCon {
            position: relative;
            z-index: 5;
        }
        #slotBg {
            background-color: var(--jh-platform-content-main-background-color, var(--jh-color-container-page));
            flex: 1;
            overflow: hidden;
        }
    `;

    @property({ type: String })
    login = "";

    resetPassword = false;
    setPassword = false;
    /** True when the URL is /set-sshkey. Set with setPassword, before the router starts. */
    setSshKey = false;
    health = false;
    idpCallback = false;


    @property() timerId: number | undefined = undefined;
    @property() logoutTimerId: number | undefined = undefined;
    private idleWatchdogId: number | undefined = undefined;
    private sessionTimeoutPromptOpen = false;

    private _removeActivityListeners?: () => void;
    private activityChannel?: BroadcastChannel;
    private activityTrackingEnabled = false; // only track when authenticated

    /**
     * Initialize LaunchDarkly with user context
     */
    private async initializeLaunchDarkly() {
        try {
            const config = getLaunchDarklyConfig();

            if (!config.enabled) {
                console.log("LaunchDarkly is disabled");
                return;
            }

            console.log("LaunchDarkly: Initializing...");
            const userContext = createUserContext();
            await launchDarkly.initialize(config.clientSideId, userContext);
            console.log("LaunchDarkly initialized successfully");
        } catch (error) {
            console.error("Failed to initialize LaunchDarkly:", error);
            // Non-blocking
        }
    }

    /**
     * Debounce helper to avoid frequent cookie writes on continuous events
     */
    private debounce<T extends (...args: any[]) => void>(fn: T, wait = 1200) {
        let t: number | undefined;
        return (...args: Parameters<T>) => {
            if (t) window.clearTimeout(t);
            t = window.setTimeout(() => fn(...args), wait);
        };
    }

    /**
     * Set last activity cookie (ISO string) and broadcast to other tabs (if supported)
     */
    private updateLastActive = () => {
        if (!this.activityTrackingEnabled) return;
        if (this.leaveIfSharedSessionCleared()) return;
        if (this.idleWindowAlreadyExpired()) {
            void this.promptSessionTimedOutAndLogout();
            return;
        }
        Cookies.set("lastActiveTime", new Date().toISOString(), { secure: true });
        this.startLogoutTimer();

        try {
            this.activityChannel?.postMessage({ type: "activity", at: Date.now() });
        } catch {
        }
        try {
            localStorage.setItem("app-activity", String(Date.now()));
            localStorage.removeItem("app-activity");
        } catch {
        }
    };

    private updateLastActiveNoBroadcast = () => {
        if (!this.activityTrackingEnabled) return;
        if (this.leaveIfSharedSessionCleared()) return;
        if (this.idleWindowAlreadyExpired()) {
            void this.promptSessionTimedOutAndLogout();
            return;
        }
        Cookies.set("lastActiveTime", new Date().toISOString(), { secure: true });
        this.startLogoutTimer();
    };

    /**
     * Shared gate for both stamp writers. Covers every activity path at once: pointer,
     * keyboard and touch events, window focus, cross-tab broadcast, and app startup
     * (`enableActivityTracking` stamps through `updateLastActive`), so a page refresh
     * cannot clear an expired stamp and hand back a usable session.
     */
    private idleWindowAlreadyExpired() {
        return hasExpiredIdleStamp(
            Cookies.get("lastActiveTime"),
            Date.now(),
            GlobalConstants.MAX_INACTIVITY_DURATION_MS
        );
    }

    private updateLastActiveDebounced = this.debounce(this.updateLastActive, 1500);

    /**
     * Optional cross-tab sync for activity updates
     */
    private setupActivityBroadcast() {
        try {
            this.activityChannel = new BroadcastChannel("app-activity");
            this.activityChannel.onmessage = (ev) => {
                if (ev?.data?.type === "session-ended") {
                    this.leaveShellAfterSessionCleared();
                    return;
                }
                if (ev?.data?.type === "activity") {
                    this.updateLastActiveNoBroadcast();
                }
            };
        } catch {
            // BroadcastChannel not supported; rely on storage events
            window.addEventListener("storage", (e) => {
                if (e.key === "app-session-ended" && e.newValue) {
                    this.leaveShellAfterSessionCleared();
                    return;
                }
                if (e.key === "app-activity" && e.newValue) {
                    this.updateLastActiveNoBroadcast();
                }
            });
        }
    }

    /** Tell other tabs the shared session cookies are about to be cleared. */
    private notifyOtherTabsSessionEnded() {
        try {
            this.activityChannel?.postMessage({ type: "session-ended" });
        } catch {
        }
        try {
            localStorage.setItem("app-session-ended", String(Date.now()));
            localStorage.removeItem("app-session-ended");
        } catch {
        }
    }

    /**
     * Idle logout clears cookies in every tab, but only the tab that opened the
     * dialog navigates. Other tabs still have this.login === "loggedIn" and their
     * watchdog returns as soon as status is gone, so send them to login.
     * The tab showing the notice (sessionTimeoutPromptOpen) stays until that notice is dismissed.
     */
    private leaveIfSharedSessionCleared() {
        if (this.sessionTimeoutPromptOpen) return false;
        if (Cookies.get("status") === "loggedIn") return false;
        this.leaveShellAfterSessionCleared();
        return true;
    }

    private leaveShellAfterSessionCleared() {
        const path = window.location.pathname.replace(/\/+$/, "");
        if (path === "/login" || path === "/callback") return;
        window.location.assign("/login");
    }

    /** IdP return replaces the session. Do not treat it as activity on the old stamp. */
    private isSignInHandoff() {
        return window.location.pathname.replace(/\/+$/, "") === "/callback";
    }

    /**
     * Register global user activity listeners
     */
    private setupActivityListeners() {
        // Avoid double registration
        if (this._removeActivityListeners) return;

        const onActive = () => this.updateLastActiveDebounced();
        const onImmediateActive = () => this.updateLastActive();

        // Core user interactions.
        // ACME-1977: "mousemove" is deliberately NOT tracked. Passive cursor movement
        // (drift, a bumped desk, a resting hand) is not intentional use, so counting it
        // kept unattended sessions alive forever and defeated the CWE-613 idle-expiration
        // control. Real use always raises one of the events below within seconds.
        window.addEventListener("mousedown", onImmediateActive, { passive: true });
        window.addEventListener("pointerdown", onImmediateActive, { passive: true });
        window.addEventListener("click", onImmediateActive, { passive: true });
        window.addEventListener("keydown", onImmediateActive);
        window.addEventListener("scroll", onActive, { passive: true });
        window.addEventListener("touchstart", onImmediateActive, { passive: true });

        // Visibility / focus handling (tab switching, window focus)
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                onImmediateActive();
            }
        };
        const onFocus = () => onImmediateActive();
        const onBlur = () => { /* no-op */ };

        // document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", onFocus);
        // window.addEventListener("blur", onBlur);

        // Initialize as active once app loads (only when enabled)
        onImmediateActive();

        // Cleanup function
        this._removeActivityListeners = () => {
            window.removeEventListener("mousedown", onImmediateActive);
            window.removeEventListener("pointerdown", onImmediateActive);
            window.removeEventListener("click", onImmediateActive);
            window.removeEventListener("keydown", onImmediateActive);
            window.removeEventListener("scroll", onActive);
            window.removeEventListener("touchstart", onImmediateActive);
            // document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", onFocus);
            // window.removeEventListener("blur", onBlur);
            this._removeActivityListeners = undefined;
        };
    }

    private enableActivityTracking() {
        if (this.activityTrackingEnabled) return;
        this.activityTrackingEnabled = true;
        this.setupActivityBroadcast();
        this.setupActivityListeners();
        this.updateLastActive(); // mark immediately
        this.startIdleWatchdog();
        if (import.meta.env.DEV) {
            void import("./utils/session-debug").then((m) => m.startSessionDebugHeartbeat());
        }
    }

    private disableActivityTracking() {
        this.activityTrackingEnabled = false;
        this.stopIdleWatchdog();
        this.clearRefreshTimer();
        this.clearLogoutTimer();
        if (import.meta.env.DEV) {
            void import("./utils/session-debug").then((m) => m.stopSessionDebugHeartbeat());
        }
        if (this._removeActivityListeners) this._removeActivityListeners();
        try {
            this.activityChannel?.close();
        } catch { }
        this.activityChannel = undefined;
    }

    /** Sync unauthenticated route shells before any await so Lit's first paint matches the URL. */
    private applyRouteShellFlags(path: string) {
        const wasIdpCallback = this.idpCallback;
        this.resetPassword = path === "/reset-password";
        this.health = path === "/health";
        this.idpCallback = path.includes("/callback");
        this.setPassword =
            path.includes("/set-password") ||
            path.includes("/set-sshkey");
        // Same path as setPassword. The first call passes window.location.pathname,
        // before router.start(), because router.page.current is still "".
        this.setSshKey = path.includes("/set-sshkey");
        // Fresh IDP return: allow Callback.ts to exchange the code once per navigation.
        // Do not clear on every callback render — that would restart the exchange.
        if (this.idpCallback && !wasIdpCallback) {
            sessionStorage.removeItem("callbackTokenFetched");
        }
    }

    async connectedCallback() {
        super.connectedCallback();

        // Apply auth shell before any await — login defaults to "" and render() shows
        // login-page until this is set. /callback and a logged-in reload (after account
        // selection) must not flash Log On while LaunchDarkly initializes.
        this.applyRouteShellFlags(window.location.pathname);
        // /login and the invite links must not inherit a signed-in shell. A Secure
        // session cookie is not cleared by a plain Cookies.remove, so drop it here.
        if (isPublicAuthPath(window.location.pathname)) {
            removeAllCookies();
            this.login = "";
        } else {
            this.login = Cookies.get("status");
        }

        // Initialize LaunchDarkly
        await this.initializeLaunchDarkly();

        setJhaWcTheme(JHA_THEMES.DESIGN_SYSTEM_INPUTS);
        this.router.routeTree.getValue().element = this;

        // Local API testing: when VITE_JHA_SSO_TOKEN is set, bypass login and use env token
        // This only runs on localhost - deployed environments use the real login flow via Callback.ts
        const envToken = (import.meta as any).env?.VITE_JHA_SSO_TOKEN;
        const isLocalhost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
        if (isLocalhost && envToken && !isPublicAuthPath(window.location.pathname)) {
            // router.start() below stamps activity. A leftover expired stamp would show the timeout dialog.
            Cookies.set("lastActiveTime", new Date().toISOString(), { secure: true });
        }
        this.router.start();

        if (isLocalhost && envToken && !isPublicAuthPath(window.location.pathname)) {
            Cookies.set("token", envToken, { secure: true });
            const expiry = new Date(Date.now() + 3600000).toISOString(); // 1 hour
            Cookies.set("expiryDateTime", expiry, { secure: true });
            Cookies.set("status", "loggedIn", { secure: true });

            // Set user cookies from env variables for local development
            // VITE_DEV_USER_TYPE allows testing as different user types (enterpriseadmin, participant, etc.)
            // VITE_DEV_USER_BU mirrors the `businessUnit` cookie the deployed sign-in flow
            // writes via setUserSessionCookies; only set when explicitly defined so the
            // localhost session matches the BU slug returned by businessunit-all-values.
            const devUserId = (import.meta as any).env?.VITE_DEV_USER_ID;
            const devUserType = (import.meta as any).env?.VITE_DEV_USER_TYPE || "participant";
            const devUserRole = (import.meta as any).env?.VITE_DEV_USER_ROLE || "ReadWrite";
            const devUserBu = (import.meta as any).env?.VITE_DEV_USER_BU;
            const devPicture = (import.meta as any).env?.VITE_DEV_PICTURE;

            console.log("[local-dev] Setting cookies - userId:", devUserId, "userType:", devUserType, "userRole:", devUserRole, "businessUnit:", devUserBu ?? "(unset)");

            if (devUserId) {
                Cookies.set("userId", encryptValue(devUserId), { secure: true });
            }

            // Mirror production behavior: EAs never carry a BU cookie. Also
            // actively clear any prior value when VITE_DEV_USER_BU is unset,
            // so switching env vars (BUA -> EA) between hot-reloads doesn't
            // leave a stale BU cookie behind.
            if (/enterpriseadmin/i.test(devUserType || "")) {
                Cookies.remove("businessUnit");
            } else if (devUserBu) {
                Cookies.set("businessUnit", String(devUserBu).trim().toLowerCase(), { secure: true });
            } else {
                Cookies.remove("businessUnit");
            }

            Cookies.set("userType", encryptValue(devUserType), { secure: true });
            Cookies.set("userRole", encryptValue(devUserRole), { secure: true });
            Cookies.set("userName", encryptValue("Local Dev"), { secure: true });
            Cookies.set("email", encryptValue("local@dev"), { secure: true });
            setPictureSessionCookie(devPicture);
        }

        // Re-read after local-dev cookie bootstrap (localhost + VITE_JHA_SSO_TOKEN).
        // Public auth routes stay logged out so Login from set-password opens the login page.
        this.applyRouteShellFlags(this.router.page.current || window.location.pathname);
        if (isPublicAuthPath(window.location.pathname)) {
            removeAllCookies();
            this.login = "";
        } else {
            this.login = Cookies.get("status");
        }

        // Enable activity tracking only on authenticated app shell.
        // /callback is replacing the session; tracking here would read the old stamp.
        if (this.login === "loggedIn" && !this.idpCallback) {
            this.enableActivityTracking();
            this.startLogoutTimer();
            this.startSessionRefreshTimer();
        } else {
            this.disableActivityTracking();
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.clearRefreshTimer();
        this.clearLogoutTimer();
        this.stopIdleWatchdog();
        this.disableActivityTracking();
    }

    private clearRefreshTimer() {
        if (this.timerId) {
            window.clearTimeout(this.timerId);
            this.timerId = undefined;
        }
    }

    private clearLogoutTimer() {
        if (this.logoutTimerId) {
            window.clearTimeout(this.logoutTimerId);
            this.logoutTimerId = undefined;
        }
    }

    startSessionRefreshTimer() {
        this.clearRefreshTimer();
        if (this.sessionTimeoutPromptOpen) return;
        if (!Cookies.get("refreshToken")) {
            if (import.meta.env.DEV) {
                void import("./utils/session-debug").then((m) =>
                    m.logSessionSnapshot("token refresh skipped (no refreshToken)")
                );
            }
            return;
        }

        const delay = sessionRefreshDelayMs(
            Cookies.get("expiryDateTime"),
            Date.now(),
            GlobalConstants.SESSION_REFRESH_BEFORE_EXPIRY_MS
        );
        if (delay == null) {
            if (import.meta.env.DEV) {
                void import("./utils/session-debug").then((m) =>
                    m.logSessionSnapshot("token refresh skipped (no/invalid expiryDateTime)")
                );
            }
            return;
        }
        if (delay === 0 && isSessionExpired()) {
            void this.promptSessionTimedOutAndLogout();
            return;
        }

        this.timerId = window.setTimeout(() => {
            void this.onSessionRefreshDue();
        }, delay);
        if (import.meta.env.DEV) {
            void import("./utils/session-debug").then((m) =>
                m.logSessionSnapshot("token refresh scheduled")
            );
        }
    }

    private async onSessionRefreshDue() {
        if (this.sessionTimeoutPromptOpen) return;
        if (import.meta.env.DEV) {
            void import("./utils/session-debug").then((m) =>
                m.logSessionSnapshot("token refresh firing")
            );
        }

        const pre = classifySessionRefreshDue({
            tokenExpired: isSessionExpired(),
            remainingIdleMs: remainingIdleMs(
                Cookies.get("lastActiveTime"),
                Date.now(),
                GlobalConstants.MAX_INACTIVITY_DURATION_MS - 30000
            ),
        });
        if (pre === "expired" || pre === "idle") {
            void this.promptSessionTimedOutAndLogout();
            return;
        }

        const sessionTimeOut = await readPostRequestPayload(
            await postRequest(Apis.REFRESH_TOKEN, {
                UserId: decryptValue(Cookies.get("userId")),
                RefreshToken: Cookies.get("refreshToken"),
                TokenUserName: decryptValue(Cookies.get("tokenUserName")),
            })
        );

        if (sessionTimeOut?.messageStatus === null) {
            const expireDate = new Date(sessionTimeOut.token.expiry);
            const expiryTimeMillisec = expireDate.getTime() - Date.now();
            Cookies.set("timeOut", String(expiryTimeMillisec), { secure: true });
            Cookies.set("expiryDateTime", sessionTimeOut.token.expiry, { secure: true });
            Cookies.set("loginTime", Date.now().toString(), { secure: true });
            Cookies.set("token", sessionTimeOut.token.token, { secure: true });
            Cookies.set("refreshToken", sessionTimeOut.token.refreshToken, { secure: true });
            setPictureSessionCookie(sessionTimeOut.token.picture);
            if (import.meta.env.DEV) {
                void import("./utils/session-debug").then((m) =>
                    m.logSessionSnapshot("token refresh succeeded")
                );
            }
            this.startSessionRefreshTimer();
            return;
        }

        if (sessionTimeOut?.details?.length > 0) {
            const errMsg = getErrorMessage(sessionTimeOut.details);
            showToast({
                message: errMsg,
                role: "danger",
                duration: GlobalVariables.TOAST_TIMEOUT,
            });
        }
        void this.promptSessionRenewalFailedAndLogout();
    }

    startLogoutTimer() {
        if (this.sessionTimeoutPromptOpen) return;
        if (this.leaveIfSharedSessionCleared()) return;

        this.clearLogoutTimer();

        const lastActiveIso = Cookies.get("lastActiveTime");
        const now = Date.now();
        const maxMs = GlobalConstants.MAX_INACTIVITY_DURATION_MS;
        if (parseLastActiveMs(lastActiveIso) != null && isIdlePastMax(lastActiveIso, now, maxMs)) {
            void this.promptSessionTimedOutAndLogout();
            return;
        }

        const delay = remainingIdleMs(lastActiveIso, now, maxMs);
        this.logoutTimerId = window.setTimeout(() => {
            this.startLogoutTimer();
        }, Math.max(delay, 1));
    }

    private startIdleWatchdog() {
        this.stopIdleWatchdog();
        this.idleWatchdogId = window.setInterval(() => this.checkIdleTimeout(), 15000);
    }

    private stopIdleWatchdog() {
        if (this.idleWatchdogId) {
            window.clearInterval(this.idleWatchdogId);
            this.idleWatchdogId = undefined;
        }
    }

    private checkIdleTimeout() {
        if (!this.activityTrackingEnabled) return;
        if (this.leaveIfSharedSessionCleared()) return;
        if (this.sessionTimeoutPromptOpen) return;
        const lastActiveIso = Cookies.get("lastActiveTime");
        if (parseLastActiveMs(lastActiveIso) == null) {
            this.updateLastActive();
            return;
        }
        if (isIdlePastMax(lastActiveIso, Date.now(), GlobalConstants.MAX_INACTIVITY_DURATION_MS)) {
            void this.promptSessionTimedOutAndLogout();
        }
    }

    private async promptSessionTimedOutAndLogout() {
        await this.promptLogoutWithNotice("sessionTimedOut");
    }

    private async promptSessionRenewalFailedAndLogout() {
        await this.promptLogoutWithNotice("sessionExpired");
    }

    private async promptLogoutWithNotice(kind: ForcedLogoutNotice) {
        if (this.sessionTimeoutPromptOpen) return;
        this.sessionTimeoutPromptOpen = true;
        this.stopIdleWatchdog();
        this.clearRefreshTimer();
        this.clearLogoutTimer();
        if (import.meta.env.DEV) {
            void import("./utils/session-debug").then((m) => {
                m.stopSessionDebugHeartbeat();
                m.logSessionSnapshot(`logout notice: ${FORCED_LOGOUT_NOTICES[kind].title}`);
            });
        }
        // ACME-1977: the session is over. Tell other tabs first, then drop credentials
        // before the notice so a hidden tab does not keep token and refreshToken
        // until the notice is dismissed. This tab stays on the dialog.
        this.notifyOtherTabsSessionEnded();
        removeAllCookies();
        await openForcedLogoutNotice(kind);
        window.location.href = "/login";
    }

    async routeEnter(
        currentNode: RouteTreeNode,
        nextNodeIfExists: RouteTreeNode | undefined,
        routeId: string,
        context: Context
    ) {
        if (this.isSignInHandoff()) {
            return super.routeEnter(currentNode, nextNodeIfExists, routeId, context);
        }

        if (this.login === "loggedIn" && Cookies.get("status") !== "loggedIn") {
            this.leaveShellAfterSessionCleared();
            return super.routeEnter(currentNode, nextNodeIfExists, routeId, context);
        }

        if (Cookies.get("status") === "loggedIn") {
            this.enableActivityTracking();
        } else {
            this.disableActivityTracking();
        }

        if (isSessionExpired()) {
            await this.promptSessionTimedOutAndLogout();
        } else if (Cookies.get("status") === "loggedIn") {
            console.log("set in router change.");
            this.updateLastActive(); // consistent ISO write + broadcast
        } else {
            // do nothing
        }
        return super.routeEnter(currentNode, nextNodeIfExists, routeId, context);
    }

    render() {
        if (this.resetPassword) {
            removeAllCookies();
            return html`
                <div id="loginBg">
                    <reset-password></reset-password>
                </div>
            `;
        } else if (this.health) {
            removeAllCookies();
            return html`
                <div id="healthbg">
                    <health-status></health-status>
                </div>
            `;
        } else if (this.idpCallback) {
            removeAllCookies();
            return html`<callback-token></callback-token>`;
        } else if (this.setPassword) {
            removeAllCookies();
            return html`
                <div id="loginBg">
                    <set-password .setSsh=${this.setSshKey}> </set-password> </div>
            `;
        } else if (this.login === "loggedIn") {
            return html`
                <div id="mainDiv">
                    <div id="mainCon">
                        <side-nav></side-nav>
                    </div>
                    <div id="slotBg">
                        <slot></slot>
                    </div>
                </div>
            `;
        } else {
            return html`
                <div id="loginBg">
                    <login-page></login-page>
                </div>
            `;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "main-element": MainElement;
    }
}
